import axios from 'axios';
import { onLogin } from '@/apollo/vue-apollo';
import apolloProvider from '@/apollo';
import Utils from '@/utils/utils';
import jwtDecode from 'jwt-decode';
import Logging from '@/logging';

const CLIENT_ID = process.env.VUE_APP_COGNITO_CLIENT_ID;
const APP_DOMAIN = process.env.VUE_APP_COGNITO_APP_DOMAIN;
const REDIRECT_URI = process.env.VUE_APP_COGNITO_REDIRECT_URI;
const USERPOOL_ID = process.env.VUE_APP_COGNITO_USERPOOL_ID;
const REDIRECT_URI_SIGNOUT = process.env.VUE_APP_COGNITO_REDIRECT_URI_SIGNOUT;

const authData = {
	ClientId: CLIENT_ID,
	AppWebDomain: APP_DOMAIN,
	TokenScopesArray: ['openid', 'email', 'profile'],
	RedirectUriSignIn: REDIRECT_URI,
	RedirectUriSignOut: REDIRECT_URI_SIGNOUT,
	UserPoolId: USERPOOL_ID
};

export default {
	login() {
		this.getSession();
	},
	logout() {
		const url = this.getSignOutUrl();
		localStorage.removeItem('rslt_access_token');
		localStorage.removeItem('rslt_id_token');
		this.signInUserSession = null;
		window.location = url;
	},
	async getSession() {
		return new Promise((resolve, reject) => {
			try {
				this.getSignInUrlAuthProxy().then(url => {
					sessionStorage.setItem('rslt_authenticating', 'true');
					sessionStorage.setItem('rslt_redirect_uri', window.location.pathname + window.location.search);
					window.location = url;
				});
			} catch {
				reject();
			}
		});
	},
	async refreshSession() {
		return new Promise((resolve, reject) => {
			try {
				const accessToken = localStorage.getItem('rslt_access_token');
				const refreshTokenPayload = {
					accessToken: accessToken
				};

				fetch(`${process.env.VUE_APP_REFRESH_TOKEN_URL}`, {
					method: 'post',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`
					},
					body: JSON.stringify(refreshTokenPayload)
				}).then((response) => {
					response.json().then((responseObject) => {
						if (responseObject && responseObject.id_token && responseObject.access_token) {
							localStorage.setItem('rslt_access_token', responseObject.access_token);
							localStorage.setItem('rslt_id_token', responseObject.id_token);

							onLogin(apolloProvider.defaultClient, responseObject.id_token);
							this.onTokensReceived();

							resolve();
						} else {
							reject();
						}
					}).catch(() => {
						reject();
					});
				}).catch(error => {
					reject(error);
				});
			} catch {
				reject();
			}
		});
	},
	async getSignInUrlAuthProxy() {
		const sha256 = async (str) => {
			return await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
		};
		const generateNonce = async () => {
			const hash = await sha256(crypto.getRandomValues(new Uint32Array(4)).toString());
			// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
			const hashArray = Array.from(new Uint8Array(hash));
			return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
		};
		const state = await generateNonce();

		return `https://${authData.AppWebDomain}/oauth2/authorize?
			response_type=code&
			client_id=${authData.ClientId}&
			state=${state}&
			redirect_uri=${authData.RedirectUriSignIn}&
			scope=${authData.TokenScopesArray.join('%20')}`;
	},
	async getSignInUrlPKCE() {
		const sha256 = async (str) => {
			return await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
		};

		const generateNonce = async () => {
			const hash = await sha256(crypto.getRandomValues(new Uint32Array(4)).toString());
			// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
			const hashArray = Array.from(new Uint8Array(hash));
			return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
		};

		const base64URLEncode = (string) => {
			return window.btoa(String.fromCharCode.apply(null, new Uint8Array(string)))
				.replace(/\+/g, '-')
				.replace(/\//g, '_')
				.replace(/=+$/, '');
		};

		const state = await generateNonce();
		const codeVerifier = await generateNonce();
		sessionStorage.setItem(`codeVerifier-${state}`, codeVerifier);
		const codeChallenge = base64URLEncode(await sha256(codeVerifier));

		return `https://${authData.AppWebDomain}/oauth2/authorize?
			response_type=code&
			client_id=${authData.ClientId}&
			state=${state}&
			code_challenge_method=S256&
			code_challenge=${codeChallenge}&
			redirect_uri=${authData.RedirectUriSignIn}`;
	},
	getSignOutUrl() {
		return `https://${authData.AppWebDomain}/logout?
			logout_uri=${authData.RedirectUriSignOut}&
			client_id=${authData.ClientId}`;
	},
	handleCognitoResponseWithAuthProxy(url) {
		return new Promise((resolve, reject) => {
			let urlParams = new URLSearchParams(url);
			const authorizationCode = urlParams.get('code');
			const redirectUri = sessionStorage.getItem('rslt_redirect_uri');
			sessionStorage.removeItem('rslt_redirect_uri');
			sessionStorage.removeItem('rslt_authenticating');

			axios
				.post(`${process.env.VUE_APP_AUTH_PROXY_URL}`, {
					authorization_code: authorizationCode
				})
				.then(async response => {
					if (response.data.access_token && response.data.id_token) {
						localStorage.setItem('rslt_access_token', response.data.access_token);
						localStorage.setItem('rslt_id_token', response.data.id_token);

						onLogin(apolloProvider.defaultClient, response.data.id_token);
						this.onTokensReceived();
						resolve(redirectUri);
					}
				})
				.catch(e => {
					reject(e);
				});
		});
	},
	handleCognitoResponse(url) {
		return new Promise((resolve, reject) => {
			let urlParams = new URLSearchParams(url);
			const authorizationCode = urlParams.get('code');
			const state = urlParams.get('state');
			const codeVerifier = sessionStorage.getItem(`codeVerifier-${state}`);
			const redirectUri = sessionStorage.getItem('rslt_redirect_uri');
			sessionStorage.removeItem('rslt_redirect_uri');
			sessionStorage.removeItem('rslt_authenticating');
			sessionStorage.removeItem(`codeVerifier-${state}`);
			if (codeVerifier === null) {
				throw new Error('Unexpected code');
			}

			const data = {
				code: authorizationCode,
				grant_type: 'authorization_code',
				redirect_uri: `${process.env.VUE_APP_URL}/login/oauth2/code/cognito`,
				code_verifier: codeVerifier,
				client_id: process.env.VUE_APP_COGNITO_CLIENT_ID
			};
			const options = {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			};
			const authRequestBody = Utils.toUrlEncoded(data).toString();
			axios
				.post(`https://${process.env.VUE_APP_COGNITO_APP_DOMAIN}/oauth2/token`, authRequestBody, options)
				.then(async response => {
					if (response.data.access_token && response.data.id_token) {
						localStorage.setItem('rslt_access_token', response.data.access_token);
						localStorage.setItem('rslt_id_token', response.data.id_token);

						onLogin(apolloProvider.defaultClient, response.data.id_token);
						this.onTokensReceived();
						resolve(redirectUri);
					}
				})
				.catch(e => {
					reject(e);
				});
		});
	},
	async checkAuthentication() {
		try {
			let idToken = localStorage.getItem('rslt_id_token');

			if (!idToken) {
			// No id_token, we need to login
				await this.getSession();
				return;
			}

			// Check if id_token is expired
			let decodedIdToken = jwtDecode(idToken);
			let exp = decodedIdToken.exp * 1000;
			let now = Date.now();
			// Difference in minutes
			const diff = (exp - now) / 1000;
			Logging.log(`Token is still active for ${diff} seconds.`);

			if (diff <= 270) { // 50% of the token lifetime
			// Token is expired, we need to refresh
				await this.refreshSession();
			} else {
				// Another window / tab refreshed the token, we can use it
				this.onTokensReceived();
			}
		} catch (e) {
			// If refresh fails, we send to login page
			await this.getSession();
		}
	},
	onTokensReceived() {
		const idToken = localStorage.getItem('rslt_id_token');
		const decodedIdToken = jwtDecode(idToken);
		const exp = decodedIdToken.exp * 1000;
		const now = Date.now();

		// Random offset between 50 and 75;
		const timeoutFactor = Math.floor(Math.random() * 26) + 50;
		const timeout = (exp - now) * (timeoutFactor / 100);
		Logging.log('Will check authentication again in ' + timeout / 1000 + ' seconds.');
		setTimeout(() => {
			this.checkAuthentication();
		}, timeout);
	}
};
